import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminRegistration } from '../../authentication_components/adminRegistrationContext';
import {
    FileText,
    Search,
    Calendar,
    User,
    Mail,
    MapPin,
    Monitor,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
    AlertCircle,
    CheckCircle,
    Clock,
    Shield
} from 'lucide-react';

export default function AdminRegistrationHistory() {
    const [registrations, setRegistrations] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        totalPages: 0,
        totalRecords: 0,
        hasNext: false,
        hasPrevious: false
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredRegistrations, setFilteredRegistrations] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const navigate = useNavigate();

    function navToDashboard() {
        navigate('/admin-dashboard');
    }
    const {
        listRegistrations,
        loading,
        error,
        clearError,
        isMasterUser
    } = useAdminRegistration();

    // Load registrations on component mount
    useEffect(() => {
        if (isMasterUser) {
            loadRegistrations(1);
        }
    }, [isMasterUser]);

    // Filter registrations based on search term
    useEffect(() => {
        if (!searchTerm) {
            setFilteredRegistrations(registrations);
        } else {
            const filtered = registrations.filter(reg =>
                reg.admin_first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                reg.admin_last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                reg.admin_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                reg.master_user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                reg.ip_address.includes(searchTerm)
            );
            setFilteredRegistrations(filtered);
        }
    }, [searchTerm, registrations]);

    const loadRegistrations = async (page = 1) => {
        try {
            setIsInitialLoad(page === 1);
            const result = await listRegistrations(page, pagination.pageSize);

            if (result.success) {
                setRegistrations(result.registrations || []);
                setPagination(result.pagination || {});
            }
        } catch (err) {
            console.error('Failed to load registrations:', err);
        } finally {
            setIsInitialLoad(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            loadRegistrations(newPage);
        }
    };

    const handleRefresh = () => {
        loadRegistrations(pagination.currentPage);
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatTime = (timeString) => {
        try {
            return timeString.substring(0, 8); // HH:MM:SS format
        } catch {
            return timeString;
        }
    };

    const formatDateTime = (dateTimeString) => {
        try {
            return new Date(dateTimeString).toLocaleString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateTimeString;
        }
    };

    const truncateText = (text, maxLength = 20) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Access control
    if (!isMasterUser) {
        return (
            <div className="bg-gradient-to-br from-red-50 to-orange-50 min-h-screen flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 border border-red-200">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                            <AlertCircle className="h-8 w-8 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            Acceso Restringido
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Solo usuarios maestros pueden ver el historial de registros.
                        </p>
                        <button
                            onClick={() => window.history.back()}
                            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
                        >
                            Volver Atrás
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 min-h-screen">
            {/* Header */}
            <div className="bg-white border-b border-orange-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <button
                                onClick={navToDashboard}
                                className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                                    <FileText className="h-6 w-6 text-orange-500 mr-3" />
                                    Historial de Registros de Administradores
                                </h2>
                                <p className="text-gray-600 mt-2">
                                    Registro completo de todos los administradores creados por usuarios maestros
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Actualizar
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Error Display */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-red-800 font-medium">Error</p>
                                <p className="text-red-700 text-sm mt-1">{error}</p>
                            </div>
                            <button
                                onClick={clearError}
                                className="text-red-400 hover:text-red-600 ml-2"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}

                {/* Search and Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-6 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre, email, usuario maestro o IP..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                />
                            </div>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                            <FileText className="h-4 w-4 mr-1" />
                            {pagination.totalRecords} registros total
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {isInitialLoad && (
                    <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-8">
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-4 border-orange-500 border-t-transparent mr-3"></div>
                            <span className="text-gray-600">Cargando historial de registros...</span>
                        </div>
                    </div>
                )}

                {/* Registration History Table */}
                {!isInitialLoad && (
                    <div className="bg-white rounded-lg shadow-sm border border-orange-200 overflow-hidden">
                        {filteredRegistrations.length === 0 ? (
                            <div className="p-8 text-center">
                                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {searchTerm ? 'No se encontraron registros' : 'No hay registros disponibles'}
                                </h3>
                                <p className="text-gray-500">
                                    {searchTerm
                                        ? 'Intente con otros términos de búsqueda'
                                        : 'Aún no se han registrado administradores'
                                    }
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop Table */}
                                <div className="hidden lg:block overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Administrador
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Contacto
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Registrado Por
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Fecha & Hora
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Detalles Técnicos
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {filteredRegistrations.map((registration, index) => (
                                                <tr key={registration.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                                                <User className="h-5 w-5 text-orange-600" />
                                                            </div>
                                                            <div className="ml-4">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {registration.admin_first_name} {registration.admin_last_name}
                                                                </div>
                                                                <div className="text-sm text-gray-500">
                                                                    ID: {registration.admin_id}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center text-sm text-gray-900">
                                                            <Mail className="h-4 w-4 text-gray-400 mr-2" />
                                                            {registration.admin_email}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <Shield className="h-4 w-4 text-red-500 mr-2" />
                                                            <span className="text-sm font-medium text-red-600">
                                                                {registration.master_user}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <div className="flex items-center">
                                                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                                            <div>
                                                                <div>{formatDate(registration.registration_date)}</div>
                                                                <div className="text-xs text-gray-500">
                                                                    {formatTime(registration.registration_time)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center">
                                                                <MapPin className="h-3 w-3 mr-1" />
                                                                <span className="text-xs">{registration.ip_address}</span>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <Monitor className="h-3 w-3 mr-1" />
                                                                <span className="text-xs" title={registration.user_agent}>
                                                                    {truncateText(registration.user_agent, 25)}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                <span className="text-xs">{formatDateTime(registration.created_at)}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Cards */}
                                <div className="lg:hidden">
                                    {filteredRegistrations.map((registration, index) => (
                                        <div key={registration.id} className="border-b border-gray-200 p-4">
                                            <div className="space-y-3">
                                                {/* Admin Info */}
                                                <div className="flex items-center">
                                                    <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                                        <User className="h-5 w-5 text-orange-600" />
                                                    </div>
                                                    <div className="ml-3">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {registration.admin_first_name} {registration.admin_last_name}
                                                        </div>
                                                        <div className="text-xs text-gray-500">ID: {registration.admin_id}</div>
                                                    </div>
                                                </div>

                                                {/* Contact */}
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <Mail className="h-4 w-4 mr-2" />
                                                    {registration.admin_email}
                                                </div>

                                                {/* Master User */}
                                                <div className="flex items-center">
                                                    <Shield className="h-4 w-4 text-red-500 mr-2" />
                                                    <span className="text-sm text-red-600 font-medium">
                                                        Registrado por: {registration.master_user}
                                                    </span>
                                                </div>

                                                {/* Date & Time */}
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <Calendar className="h-4 w-4 mr-2" />
                                                    {formatDate(registration.registration_date)} - {formatTime(registration.registration_time)}
                                                </div>

                                                {/* Technical Details */}
                                                <div className="text-xs text-gray-500 space-y-1">
                                                    <div className="flex items-center">
                                                        <MapPin className="h-3 w-3 mr-1" />
                                                        IP: {registration.ip_address}
                                                    </div>
                                                    <div className="flex items-center">
                                                        <Clock className="h-3 w-3 mr-1" />
                                                        Creado: {formatDateTime(registration.created_at)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination */}
                                {pagination.totalPages > 1 && (
                                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 flex justify-between sm:hidden">
                                                <button
                                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                    disabled={!pagination.hasPrevious || loading}
                                                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Anterior
                                                </button>
                                                <button
                                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                    disabled={!pagination.hasNext || loading}
                                                    className="ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Siguiente
                                                </button>
                                            </div>
                                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-700">
                                                        Mostrando{' '}
                                                        <span className="font-medium">
                                                            {(pagination.currentPage - 1) * pagination.pageSize + 1}
                                                        </span>{' '}
                                                        a{' '}
                                                        <span className="font-medium">
                                                            {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalRecords)}
                                                        </span>{' '}
                                                        de{' '}
                                                        <span className="font-medium">{pagination.totalRecords}</span> registros
                                                    </p>
                                                </div>
                                                <div>
                                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                                        <button
                                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                            disabled={!pagination.hasPrevious || loading}
                                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <ChevronLeft className="h-5 w-5" />
                                                        </button>

                                                        {/* Page Numbers */}
                                                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                            let pageNum;
                                                            if (pagination.totalPages <= 5) {
                                                                pageNum = i + 1;
                                                            } else if (pagination.currentPage <= 3) {
                                                                pageNum = i + 1;
                                                            } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                                                pageNum = pagination.totalPages - 4 + i;
                                                            } else {
                                                                pageNum = pagination.currentPage - 2 + i;
                                                            }

                                                            return (
                                                                <button
                                                                    key={pageNum}
                                                                    onClick={() => handlePageChange(pageNum)}
                                                                    disabled={loading}
                                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pageNum === pagination.currentPage
                                                                            ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                                                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            );
                                                        })}

                                                        <button
                                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                            disabled={!pagination.hasNext || loading}
                                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <ChevronRight className="h-5 w-5" />
                                                        </button>
                                                    </nav>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}